/**
 * useOfferContext — resolves the `addToOfferId` URL param into an offer-context
 * summary shared by the catalog (`/media-units`) and map (`/map`) routes.
 *
 * Deliberately resolves via `offerQueries.detail` (`GET /offers/:offerId`), NOT
 * the 50-capped `useEditableDraftsQuery` list — an editable offer outside that
 * list's top 50 must still resolve to a valid context.
 *
 * "No context" (offerContext: null, isError: false) covers every expected-invalid
 * case: missing param, 404, 403, trashed, or not editable. Offer STATUS is
 * deliberately not part of the gate: an editable non-trashed offer of any
 * status enters offer context, matching `deriveWorkspaceShellAccess`'s
 * `canEdit` (see workspace-access.ts), which never checks `status`.
 * A transient fetch failure (network/5xx) is kept as a retryable `isError` state
 * instead — the caller must not collapse it into "no context".
 */
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import type { OfferDetail } from '@ooh/contracts';
import { parseApiError } from '@ooh/contracts';
import { offerQueries } from '../../api/queries';
import { deriveWorkspaceShellAccess } from '../../lib/workspace-access';
import { useHasPermission } from '../../../../hooks/use-has-permission';

export interface OfferContext {
  offerId: string;
  offerName: string;
}

export interface OfferContextResult {
  offerContext: OfferContext | null;
  isPending: boolean;
  isError: boolean;
}

const NO_CONTEXT: OfferContextResult = { offerContext: null, isPending: false, isError: false };

/** Editable (any status) + not-trashed -> context; everything else -> no context. */
function resolveOfferContext(
  offer: OfferDetail,
  offerId: string,
  userId: number | null,
  canManageAny: boolean,
): OfferContext | null {
  const { canEdit } = deriveWorkspaceShellAccess({
    canApproveAny: false,
    canManageAny,
    canSetStatusAny: false,
    offer,
    userId,
  });
  if (!canEdit) return null;
  return { offerId, offerName: offer.title };
}

/**
 * 404 (no such offer) / 403 (no access) are expected-invalid -> no context.
 * Anything else (network failure, 5xx) is a transient, retryable error.
 */
function resolveErrorState(error: unknown): OfferContextResult {
  const statusCode = parseApiError(error).statusCode;
  if (statusCode === 404 || statusCode === 403) return NO_CONTEXT;
  return { offerContext: null, isPending: false, isError: true };
}

export function useOfferContext(addToOfferId: string | undefined): OfferContextResult {
  const { auth } = useRouteContext({ from: '__root__' });
  // Fail-closed while loading: a wrongly-closed context (falls back to the
  // standard screen) is far less harmful than a wrongly-open one.
  const canManageAny = useHasPermission('offer.view_all', { loading: 'closed' });
  const enabled = addToOfferId != null;
  const query = useQuery({
    ...offerQueries.detail(auth, addToOfferId ?? ''),
    enabled,
  });

  if (!enabled) return NO_CONTEXT;
  if (query.isPending) return { offerContext: null, isPending: true, isError: false };
  if (query.isError) return resolveErrorState(query.error);

  const userId = auth.user?.id ?? null;
  return {
    offerContext: resolveOfferContext(query.data, addToOfferId ?? '', userId, canManageAny),
    isPending: false,
    isError: false,
  };
}
